import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  Chip,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { getModuleTableTheme, modernCellStyles } from '../../../theme/tableTheme';
import useAlert from '../../../hooks/useAlert';
import AlertDialog from '../../../components/AlertDialog';
import { extractErrorMessage } from '../../../utils/errorHandler';
import {
  commercialVisibilityAPI,
  CommercialVisibilityUser,
  ViewerVisibility,
} from '../../../services/api';

const ADMIN_COLOR = '#F44336';

const CommercialVisibilityManagement: React.FC = () => {
  const [users, setUsers] = useState<CommercialVisibilityUser[]>([]);
  const [grants, setGrants] = useState<ViewerVisibility[]>([]);
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewer, setViewer] = useState<CommercialVisibilityUser | null>(null);
  const [selectedOwners, setSelectedOwners] = useState<CommercialVisibilityUser[]>([]);
  const [saving, setSaving] = useState(false);

  const { alertState, showError, showSuccess, hideAlert } = useAlert();

  const usersById = useMemo(() => {
    const map = new Map<number, CommercialVisibilityUser>();
    users.forEach((u) => map.set(u.id, u));
    return map;
  }, [users]);

  const grantsByViewer = useMemo(() => {
    const map = new Map<number, number[]>();
    grants.forEach((g) => map.set(g.viewer_user_id, g.owner_user_ids));
    return map;
  }, [grants]);

  // Only non-manager users are meaningful viewers (managers already see everything).
  const viewerCandidates = useMemo(
    () => users.filter((u) => !u.is_manager),
    [users]
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersData, grantsData] = await Promise.all([
        commercialVisibilityAPI.getUsers(),
        commercialVisibilityAPI.getAll(),
      ]);
      setUsers(usersData);
      setGrants(grantsData);
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEdit = (user: CommercialVisibilityUser) => {
    setViewer(user);
    const ownerIds = grantsByViewer.get(user.id) ?? [];
    setSelectedOwners(
      ownerIds.map((id) => usersById.get(id)).filter(Boolean) as CommercialVisibilityUser[]
    );
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setViewer(null);
    setSelectedOwners([]);
  };

  const handleSave = async () => {
    if (!viewer) return;
    setSaving(true);
    try {
      await commercialVisibilityAPI.setForViewer(
        viewer.id,
        selectedOwners.map((o) => o.id)
      );
      showSuccess(`Visibilité mise à jour pour ${viewer.full_name}`);
      handleClose();
      fetchData();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  // Owner options for the dialog: any user except the viewer themselves.
  const ownerOptions = useMemo(
    () => users.filter((u) => u.id !== viewer?.id),
    [users, viewer]
  );

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <VisibilityIcon sx={{ color: ADMIN_COLOR }} />
        <Typography variant="h5" sx={{ fontWeight: 600, color: ADMIN_COLOR }}>
          Visibilité Commerciale
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configurez quels commerciaux peuvent voir (et gérer) les données d'autres
        commerciaux. Chaque utilisateur voit toujours ses propres données ; les
        managers voient tout.
      </Typography>

      <Paper elevation={0}>
        <TableContainer sx={getModuleTableTheme('administration')}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={modernCellStyles}>Commercial</TableCell>
                <TableCell sx={modernCellStyles}>Peut voir les données de</TableCell>
                <TableCell sx={modernCellStyles} align="right">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {viewerCandidates.map((user) => {
                const ownerIds = grantsByViewer.get(user.id) ?? [];
                return (
                  <TableRow key={user.id} hover>
                    <TableCell sx={modernCellStyles}>
                      <Typography sx={{ fontWeight: 500 }}>{user.full_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user.role || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={modernCellStyles}>
                      {ownerIds.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Ses données uniquement
                        </Typography>
                      ) : (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {ownerIds.map((id) => (
                            <Chip
                              key={id}
                              size="small"
                              label={usersById.get(id)?.full_name ?? `#${id}`}
                              sx={{ bgcolor: '#FFEBEE', color: ADMIN_COLOR }}
                            />
                          ))}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell sx={modernCellStyles} align="right">
                      <Tooltip title="Configurer la visibilité">
                        <IconButton onClick={() => handleEdit(user)} size="small">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!loading && viewerCandidates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Aucun commercial à configurer.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <span>Visibilité de {viewer?.full_name}</span>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sélectionnez les commerciaux dont {viewer?.full_name} pourra voir et
            gérer les données (devis, BL, factures, avoirs, proformas).
          </Typography>
          <Autocomplete
            multiple
            options={ownerOptions}
            value={selectedOwners}
            onChange={(_e, value) => setSelectedOwners(value)}
            getOptionLabel={(o) => o.full_name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField {...params} label="Données visibles" placeholder="Ajouter un commercial" />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box>
                  <Typography>{option.full_name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.role || '—'}
                  </Typography>
                </Box>
              </li>
            )}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} variant="outlined">
            Annuler
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            sx={{ backgroundColor: ADMIN_COLOR, '&:hover': { backgroundColor: '#D32F2F' } }}
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
      />
    </Box>
  );
};

export default CommercialVisibilityManagement;
